import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, FormControl, MenuItem, Select, Typography } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import Pagination from '../../../components/atoms/Pagination';
import { dashboardTokens } from '../../Dashboard/Components/dashboardCardStyles';
import { leadStatusMeta, platformMeta } from './leadsMeta';
import { LeadsListTable } from './LeadsListTable';
import {
  AdPlatform,
  Campaign,
  MarketingLead,
  MarketingLeadStatus,
  getMarketingLeads,
} from '../../../services/marketingLeads';

const PLATFORM_OPTIONS: AdPlatform[] = ['META', 'GOOGLE', 'ORGANIC', 'OTHER'];
const STATUS_OPTIONS: MarketingLeadStatus[] = ['NEW', 'CONTACTED', 'QUALIFIED', 'CONVERTED', 'LOST'];

const selectSx = {
  minWidth: 150,
  color: '#e2e8f0',
  bgcolor: '#1c1c1c',
  '.MuiOutlinedInput-notchedOutline': { borderColor: dashboardTokens.border },
  '.MuiSvgIcon-root': { color: '#94a3b8' },
};

interface LeadsTableProps {
  campaigns: Campaign[];
  platformFilter: AdPlatform | '';
  onPlatformFilterChange: (platform: AdPlatform | '') => void;
  // Bump to force a refetch — e.g. after a Meta history import lands new
  // rows while this table is already mounted behind the dialog.
  refreshKey?: number;
}

export const LeadsTable: React.FC<LeadsTableProps> = ({
  campaigns,
  platformFilter,
  onPlatformFilterChange,
  refreshKey = 0,
}) => {
  const [leads, setLeads] = useState<MarketingLead[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [campaignId, setCampaignId] = useState('');
  const [status, setStatus] = useState<MarketingLeadStatus | ''>('');
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const navigate = useNavigate();

  const fetchLeads = useCallback(async () => {
    try {
      setLoading(true);
      const result = await getMarketingLeads({
        page,
        limit: rowsPerPage,
        source: platformFilter || undefined,
        campaignId: campaignId || undefined,
        status: status || undefined,
      });
      setLeads(result.data);
      setTotal(result.total);
    } catch (error) {
      console.error('Failed to load leads:', error);
      setLeads([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, platformFilter, campaignId, status]);

  useEffect(() => {
    fetchLeads();
  }, [fetchLeads, refreshKey]);

  // Carries the active source through so the full list opens pre-filtered
  // rather than resetting what the user just chose.
  const handleViewAll = () => {
    navigate(platformFilter ? `/leads/all?source=${platformFilter}` : '/leads/all');
  };

  return (
    <Box
      sx={{
        bgcolor: dashboardTokens.cardBg,
        border: `1px solid ${dashboardTokens.border}`,
        borderRadius: dashboardTokens.cardRadius,
        p: 2.5,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 2,
          flexWrap: 'wrap',
          gap: 1.5,
        }}
      >
        <Typography sx={{ fontSize: 16, fontWeight: 700, color: '#fff' }}>All Leads</Typography>
        <Box sx={{ display: 'flex', gap: 1.25, flexWrap: 'wrap' }}>
          <FormControl size="small">
            <Select
              displayEmpty
              value={platformFilter}
              onChange={(e) => {
                onPlatformFilterChange(e.target.value as AdPlatform | '');
                setPage(1);
              }}
              sx={selectSx}
            >
              <MenuItem value="">Source</MenuItem>
              {PLATFORM_OPTIONS.map((p) => (
                <MenuItem key={p} value={p}>
                  {platformMeta[p].label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small">
            <Select
              displayEmpty
              value={campaignId}
              onChange={(e) => {
                setCampaignId(e.target.value);
                setPage(1);
              }}
              sx={selectSx}
            >
              <MenuItem value="">Campaign</MenuItem>
              {campaigns.map((c) => (
                <MenuItem key={c._id} value={c._id}>
                  {c.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small">
            <Select
              displayEmpty
              value={status}
              onChange={(e) => {
                setStatus(e.target.value as MarketingLeadStatus | '');
                setPage(1);
              }}
              sx={selectSx}
            >
              <MenuItem value="">Status</MenuItem>
              {STATUS_OPTIONS.map((s) => (
                <MenuItem key={s} value={s}>
                  {leadStatusMeta[s].label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Box>

      <Box sx={{ overflowX: 'auto' }}>
        <LeadsListTable
          leads={leads}
          isLoading={loading}
          noDataText="No leads found for the selected filters yet."
          onConverted={fetchLeads}
        />
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1 }}>
        <Box
          onClick={handleViewAll}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            fontSize: 13,
            fontWeight: 700,
            color: '#60a5fa',
            cursor: 'pointer',
            '&:hover': { textDecoration: 'underline' },
          }}
        >
          View All Leads <ArrowForwardIcon sx={{ fontSize: 15 }} />
        </Box>
        <Pagination
          count={total}
          page={page - 1}
          rowsPerPage={rowsPerPage}
          onPageChange={(_: unknown, newPage: number) => setPage(newPage + 1)}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(parseInt(event.target.value, 10));
            setPage(1);
          }}
        />
      </Box>
    </Box>
  );
};
